import { useEffect, useRef, useState } from 'react';
import { EMBED_TO_PARENT_MESSAGES } from 'src/constants/EmbedMessageConstants';
import { sanitizeReportedPath } from 'src/components/das-finsuite/embedded-apps/utils/EmbeddingUtils';
import { logger } from 'src/logger';

/**
 * Listens for FINSUITE_CURRENT_PATH messages from the active embedded iframe and returns
 * the latest sanitized relative path the app reported (or null when none/invalid).
 *
 * SECURITY — a reported path is accepted only when ALL hold (mirrors useEmbeddedSearch):
 *   1. event.source is the embedded iframe's contentWindow (not some other frame)
 *   2. event.origin equals the embedded app's own origin (derived from baseUrl)
 *   3. the message type is FINSUITE_CURRENT_PATH and payload.path is a safe relative path
 *
 * Resets to null whenever baseUrl changes (e.g. an environment switch) or on unmount, so a
 * stale path from a previous app/environment can never leak into the external link.
 *
 * @param baseUrl - Resolved base URL of the currently embedded app, or null when none.
 * @returns The latest reported relative path (pathname+search+hash), or null.
 */
export const useEmbeddedCurrentPath = (baseUrl: string | null): string | null => {
  const [reportedPath, setReportedPath] = useState<string | null>(null);

  // Keep the expected origin in a ref so the listener always reads the latest value
  // without needing to re-subscribe on every base-URL change.
  const expectedOriginRef = useRef<string | null>(null);
  useEffect(() => {
    try {
      expectedOriginRef.current = baseUrl ? new URL(baseUrl).origin : null;
    } catch {
      expectedOriginRef.current = null;
    }
    // New app/env → drop any path reported by the previous one.
    setReportedPath(null);
  }, [baseUrl]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const { type, payload } = event.data || {};
      if (type !== EMBED_TO_PARENT_MESSAGES.CURRENT_PATH) return;

      // 1. Source must be the embedded iframe itself.
      const iframe = document.querySelector<HTMLIFrameElement>('iframe[data-embed-app]');
      if (!iframe || event.source !== iframe.contentWindow) {
        logger.warn('FINSUITE_CURRENT_PATH ignored — message source is not the embedded iframe');
        return;
      }

      // 2. Origin must match the embedded app's own origin.
      const expectedOrigin = expectedOriginRef.current;
      if (!expectedOrigin || event.origin !== expectedOrigin) {
        logger.warn(`FINSUITE_CURRENT_PATH ignored — origin "${event.origin}" !== expected "${expectedOrigin}"`);
        return;
      }

      // 3. Payload must be a safe relative path.
      const safePath = sanitizeReportedPath(payload?.path);
      if (!safePath) {
        logger.warn(`FINSUITE_CURRENT_PATH ignored — unsafe or missing path: ${JSON.stringify(payload?.path)}`);
        return;
      }

      // Accepted-path logging is debug-level: this fires once per in-app navigation, so at
      // info it would be per-click Katal/CloudWatch noise with no error signal. The rejection
      // paths above stay at warn — those are the ones worth surfacing.
      logger.debug(`FINSUITE_CURRENT_PATH accepted — embedded app reported path: ${safePath}`);
      setReportedPath(safePath);
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  return reportedPath;
};
